// LaundryOrders.js  Order list with status workflow + new order dialog.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Card, CardContent, Paper, Stack, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Snackbar, Alert, CircularProgress, MenuItem,
  Divider, Tooltip, FormControlLabel, Switch, Stepper, Step, StepLabel,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HotelIcon from '@mui/icons-material/Hotel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { laundryApi, lookupApi, ctx } from './modulesApi';

const STATUS_FLOW = ['Pending', 'Picked', 'Washing', 'Ironing', 'Ready', 'Delivered'];
const NEXT_STATUS = {
  Pending: 'Picked', Picked: 'Washing', Washing: 'Ironing',
  Ironing: 'Ready', Ready: 'Delivered',
};

export default function LaundryOrders() {
  const { companyId, hotelId, userId, currency } = ctx();
  const theme = useTheme();
  // Dialogs go edge-to-edge on phones (< sm); windowed on tablet/desktop.
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  // Phones (< md) get a card list; tablet/desktop keep the full table.
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [items_, setItems_] = useState([]); // master items
  const [guests, setGuests] = useState([]);
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [form, setForm] = useState({
    guestName: '', guestMobile: '', roomNumber: '', bookingId: null,
    pickupDate: '', discountAmount: 0, chargeToRoom: false, remarks: '',
    // Start with one empty line so the user can add an item straight away
    // without first clicking "Add line".
    items: [{ laundryItemId: 0, quantity: 1 }],
  });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await laundryApi.listOrders(hotelId, {
        status: statusFilter || undefined,
        page: page + 1, pageSize: rowsPerPage,
      });
      setItems(r.data.items || []); setTotal(r.data.totalCount || 0);
    } catch { notify('Load failed', 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage, statusFilter]);

  const openCreate = async () => {
    const [itemsRes, guestsRes] = await Promise.all([
      laundryApi.listItems(hotelId, { pageSize: 200 }),
      lookupApi.inHouseGuests(hotelId).catch((e) => {
        console.error('[InHouseGuests] failed', e);
        return { data: [] };
      }),
    ]);
    setItems_(itemsRes.data.items || []);
    setGuests(guestsRes.data || []);
    setSelectedGuestId('');
    setForm({ guestName: '', guestMobile: '', roomNumber: '', bookingId: null,
              pickupDate: '', discountAmount: 0, chargeToRoom: false, remarks: '',
              items: [{ laundryItemId: 0, quantity: 1 }] });
    setOpen(true);
  };

  const onPickGuest = (bookingId) => {
    setSelectedGuestId(bookingId);
    if (!bookingId) {
      setForm({ ...form, bookingId: null, guestName: '', guestMobile: '', roomNumber: '', chargeToRoom: false });
      return;
    }
    const g = guests.find((x) => x.bookingId === bookingId);
    if (!g) return;
    setForm({
      ...form,
      bookingId: g.bookingId,
      guestName: g.guestName,
      guestMobile: g.mobileNumber || '',
      roomNumber: g.roomNumber || '',
      chargeToRoom: true,
    });
  };

  const addLine = () => setForm({ ...form, items: [...form.items, { laundryItemId: 0, quantity: 1 }] });
  const removeLine = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateLine = (idx, patch) => {
    setForm({ ...form, items: form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  };

  // GST policy: every line uses its own item master GST% — same rate for
  // walk-in and charge-to-room (mirrors backend).
  const totals = useMemo(() => {
    let sub = 0, itemGst = 0;
    form.items.forEach((line) => {
      const m = items_.find((i) => i.laundryItemId === line.laundryItemId);
      if (!m) return;
      const ls = m.price * (line.quantity || 0);
      sub += ls;
      itemGst += ls * (m.gstPercent / 100);
    });
    const disc = parseFloat(form.discountAmount) || 0;
    return { sub, gst: itemGst, tot: sub + itemGst - disc };
  }, [form.items, form.discountAmount, items_]);

  const onCreate = async () => {
    if (!form.guestName) return notify('Guest name required', 'warning');
    if (form.items.length === 0) return notify('Add items', 'warning');
    if (form.items.some((i) => !i.laundryItemId || !i.quantity)) return notify('Fill all lines', 'warning');
    try {
      const dto = {
        companyId, hotelId, bookingId: form.bookingId,
        guestName: form.guestName, guestMobile: form.guestMobile,
        roomNumber: form.roomNumber, pickupDate: form.pickupDate || null,
        discountAmount: parseFloat(form.discountAmount) || 0,
        chargeToRoom: form.chargeToRoom, remarks: form.remarks,
        createdBy: userId,
        items: form.items.map((l) => ({ laundryItemId: l.laundryItemId, quantity: parseInt(l.quantity, 10) })),
      };
      await laundryApi.createOrder(dto);
      notify('Order created'); setOpen(false); load();
    } catch { notify('Create failed', 'error'); }
  };

  const onAdvance = async (order) => {
    const to = NEXT_STATUS[order.status];
    if (!to) return;
    try {
      await laundryApi.changeStatus({ laundryOrderId: order.laundryOrderId, toStatus: to, changedBy: userId });
      notify(`Status: ${order.status}  ${to}`); load();
    } catch (e) {
      notify(e.response?.data?.error || 'Status change failed', 'error');
    }
  };

  const onCancel = async (order) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      await laundryApi.changeStatus({
        laundryOrderId: order.laundryOrderId, toStatus: 'Cancelled',
        changedBy: userId, remarks: 'User cancelled',
      });
      notify('Cancelled'); load();
    } catch { notify('Failed', 'error'); }
  };

  const openHistory = async (order) => {
    const r = await laundryApi.history(order.laundryOrderId);
    setHistory(r.data || []); setHistoryOpen(true);
  };

  const openDetails = async (order) => {
    setDetailsOpen(true);
    setDetailsOrder(null);
    setDetailsLoading(true);
    try {
      const r = await laundryApi.getOrder(order.laundryOrderId);
      setDetailsOrder(r.data);
    } catch { notify('Failed to load details', 'error'); }
    finally { setDetailsLoading(false); }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3, px: { xs: 1.5, sm: 3 } }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1} mb={2}>
            <Typography variant='h5'>Laundry Orders</Typography>
            <Button variant='contained' startIcon={<AddIcon />} onClick={openCreate}>New Order</Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth size='small' label='Status' value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value=''>All</MenuItem>
                {[...STATUS_FLOW, 'Cancelled'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          {isPhone ? (
            <Stack spacing={1.2}>
              {loading && (<Box textAlign='center' py={2}><CircularProgress size={24} /></Box>)}
              {!loading && items.length === 0 && (
                <Typography align='center' color='text.secondary' py={2}>No orders</Typography>
              )}
              {!loading && items.map((o) => (
                <Paper key={o.laundryOrderId} variant='outlined' sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', wordBreak: 'break-all' }}>{o.orderNo}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        {o.guestName}{o.roomNumber ? ` · Room ${o.roomNumber}` : ''}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {o.bookingId ? `Booking #${o.bookingId}` : 'Walk-in'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Chip size='small' label={o.status}
                        color={o.status === 'Delivered' ? 'success'
                              : o.status === 'Cancelled' ? 'error'
                              : o.status === 'Ready' ? 'info' : 'warning'} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mt: 0.5 }}>
                        {currency} {Number(o.totalAmount).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                      {new Date(o.orderDate).toLocaleDateString('en-GB')}
                      {o.expectedDeliveryDate ? ` → ${new Date(o.expectedDeliveryDate).toLocaleDateString('en-GB')}` : ''}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                      <Tooltip title='View Items'>
                        <IconButton size='small' onClick={() => openDetails(o)}><VisibilityIcon fontSize='small' /></IconButton>
                      </Tooltip>
                      <Tooltip title='Advance status'>
                        <span>
                          <IconButton size='small' disabled={!NEXT_STATUS[o.status]} onClick={() => onAdvance(o)}><ArrowForwardIcon fontSize='small' /></IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='History'>
                        <IconButton size='small' onClick={() => openHistory(o)}><HistoryIcon fontSize='small' /></IconButton>
                      </Tooltip>
                      <Tooltip title='Cancel'>
                        <span>
                          <IconButton size='small' color='error' disabled={['Delivered', 'Cancelled'].includes(o.status)} onClick={() => onCancel(o)}><DeleteIcon fontSize='small' /></IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table size='small' sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell><TableCell>Booking #</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Room</TableCell><TableCell>Order Date</TableCell>
                  <TableCell>Expected</TableCell><TableCell>Status</TableCell>
                  <TableCell align='right'>Total</TableCell><TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (<TableRow><TableCell colSpan={9} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
                {!loading && items.length === 0 && (<TableRow><TableCell colSpan={9} align='center'>No orders</TableCell></TableRow>)}
                {items.map((o) => (
                  <TableRow key={o.laundryOrderId} hover>
                    <TableCell>{o.orderNo}</TableCell>
                    <TableCell>{o.bookingId || 'Walk-in'}</TableCell>
                    <TableCell>{o.guestName}</TableCell>
                    <TableCell>{o.roomNumber}</TableCell>
                    <TableCell>{new Date(o.orderDate).toLocaleString('en-GB')}</TableCell>
                    <TableCell>{o.expectedDeliveryDate ? new Date(o.expectedDeliveryDate).toLocaleString('en-GB') : ''}</TableCell>
                    <TableCell>
                      <Chip size='small' label={o.status}
                        color={o.status === 'Delivered' ? 'success'
                              : o.status === 'Cancelled' ? 'error'
                              : o.status === 'Ready' ? 'info' : 'warning'} />
                    </TableCell>
                    <TableCell align='right'>{currency} {Number(o.totalAmount).toFixed(2)}</TableCell>
                    <TableCell align='right'>
                      <Tooltip title='View Items'>
                        <IconButton size='small' onClick={() => openDetails(o)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Advance status'>
                        <span>
                          <IconButton size='small' disabled={!NEXT_STATUS[o.status]} onClick={() => onAdvance(o)}>
                            <ArrowForwardIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='History'>
                        <IconButton size='small' onClick={() => openHistory(o)}><HistoryIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title='Cancel'>
                        <span>
                          <IconButton size='small' color='error'
                            disabled={['Delivered', 'Cancelled'].includes(o.status)} onClick={() => onCancel(o)}>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          <TablePagination component='div' count={total} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 0.5 } }} />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>New Laundry Order</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size='small'
                label='In-house guest (auto-fills + charges to room)'
                value={selectedGuestId}
                onChange={(e) => onPickGuest(e.target.value)}
                InputProps={{ startAdornment: <HotelIcon fontSize='small' sx={{ mr: 1, color: 'action.active' }} /> }}>
                <MenuItem value=''>--Walk-in / manual--</MenuItem>
                {guests.map((g) => (
                  <MenuItem key={g.bookingId} value={g.bookingId}>
                    Room {g.roomNumber}  {g.guestName} (#{g.bookingId})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label='Guest Name' value={form.guestName}
              onChange={(e) => setForm({ ...form, guestName: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label='Mobile' value={form.guestMobile}
              onChange={(e) => setForm({ ...form, guestMobile: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label='Room' value={form.roomNumber}
              onChange={(e) => setForm({ ...form, roomNumber: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth type='datetime-local' InputLabelProps={{ shrink: true }}
              label='Pickup Date' value={form.pickupDate}
              onChange={(e) => setForm({ ...form, pickupDate: e.target.value })} /></Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth type='number' label='Discount' value={form.discountAmount}
              onChange={(e) => setForm({ ...form, discountAmount: e.target.value })} /></Grid>
            <Grid item xs={6} sm={4}><FormControlLabel control={
              <Switch checked={form.chargeToRoom} onChange={(e) => setForm({ ...form, chargeToRoom: e.target.checked })} />
            } label='Charge to room' /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }}>Items</Divider>
          <Box display='flex' justifyContent='flex-end' mb={1}>
            <Button size='small' startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          </Box>

          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table size='small' sx={{ minWidth: 760 }}>
              <TableHead><TableRow>
                <TableCell>Item</TableCell><TableCell align='right'>Qty</TableCell>
                <TableCell align='right'>Unit Price</TableCell><TableCell align='right'>Line Total</TableCell>
                <TableCell />
              </TableRow></TableHead>
              <TableBody>
                {form.items.map((line, idx) => {
                  const m = items_.find((i) => i.laundryItemId === line.laundryItemId);
                  const rate = m ? m.gstPercent / 100 : 0;
                  const lineTotal = m ? (m.price * (line.quantity || 0)) * (1 + rate) : 0;
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField select size='small' value={line.laundryItemId} sx={{ minWidth: 200 }}
                          onChange={(e) => updateLine(idx, { laundryItemId: parseInt(e.target.value, 10) })}>
                          <MenuItem value={0}>--Select--</MenuItem>
                          {items_.map((i) => (
                            <MenuItem key={i.laundryItemId} value={i.laundryItemId}>
                              {i.itemName} ({i.washType})
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align='right'>
                        <TextField size='small' type='number' value={line.quantity}
                          onChange={(e) => updateLine(idx, { quantity: parseInt(e.target.value, 10) || 0 })}
                          sx={{ width: 80 }} />
                      </TableCell>
                      <TableCell align='right'>{m ? m.price.toFixed(2) : '0.00'}</TableCell>
                      <TableCell align='right'>{lineTotal.toFixed(2)}</TableCell>
                      <TableCell><IconButton size='small' color='error' onClick={() => removeLine(idx)}><DeleteIcon /></IconButton></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 3 }, mt: 2 }}>
            <Typography>Sub: <strong>{currency} {totals.sub.toFixed(2)}</strong></Typography>
            <Typography>GST: <strong>{currency} {totals.gst.toFixed(2)}</strong></Typography>
            {(parseFloat(form.discountAmount) || 0) > 0 && (
              <Typography color='error'>Disc:  {currency} {(parseFloat(form.discountAmount) || 0).toFixed(2)}</Typography>
            )}
            <Typography>Total: <strong>{currency} {totals.tot.toFixed(2)}</strong></Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onCreate}>Create Order</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth='md' fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>
          Laundry Order Details
          {detailsOrder?.bookingId ? ` — Linked Booking #${detailsOrder.bookingId}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading && <CircularProgress size={24} />}
          {!detailsLoading && detailsOrder && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}><strong>Order #:</strong> {detailsOrder.orderNo}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Guest:</strong> {detailsOrder.guestName}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Mobile:</strong> {detailsOrder.guestMobile || '-'}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Room:</strong> {detailsOrder.roomNumber || '-'}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Booking #:</strong> {detailsOrder.bookingId || 'Walk-in'}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Status:</strong> {detailsOrder.status}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Order Date:</strong> {new Date(detailsOrder.orderDate).toLocaleString('en-GB')}</Grid>
                <Grid item xs={12} sm={6} md={3}><strong>Charge to Room:</strong> {detailsOrder.chargeToRoom ? 'Yes' : 'No'}</Grid>
              </Grid>
              <Divider sx={{ my: 2 }}>Items</Divider>
              <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                <Table size='small' sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Wash Type</TableCell>
                      <TableCell align='right'>Qty</TableCell>
                      <TableCell align='right'>Unit Price</TableCell>
                      <TableCell align='right'>GST</TableCell>
                      <TableCell align='right'>Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailsOrder.items || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.itemName || `#${it.laundryItemId}`}</TableCell>
                        <TableCell>{it.washType || '-'}</TableCell>
                        <TableCell align='right'>{it.quantity}</TableCell>
                        <TableCell align='right'>{currency} {Number(it.unitPrice).toFixed(2)}</TableCell>
                        <TableCell align='right'>{currency} {Number(it.gstAmount).toFixed(2)}</TableCell>
                        <TableCell align='right'>{currency} {Number(it.lineTotal).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 3 }, mt: 2 }}>
                <Typography>Subtotal: <strong>{currency} {Number(detailsOrder.subTotal).toFixed(2)}</strong></Typography>
                <Typography>GST: <strong>{currency} {Number(detailsOrder.gstAmount).toFixed(2)}</strong></Typography>
                {Number(detailsOrder.discountAmount) > 0 && (
                  <Typography color='error'>Discount:  {currency} {Number(detailsOrder.discountAmount).toFixed(2)}</Typography>
                )}
                <Typography variant='h6'>Total: <strong>{currency} {Number(detailsOrder.totalAmount).toFixed(2)}</strong></Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth='sm' fullWidth fullScreen={fullScreenDialog}>
        <DialogTitle>Status History</DialogTitle>
        <DialogContent dividers>
          <Stepper alternativeLabel activeStep={history.length}>
            {history.map((h, i) => (
              <Step key={i} completed>
                <StepLabel>
                  {h.toStatus}<br />
                  <small>{new Date(h.changedAt).toLocaleString('en-GB')}</small>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
        <DialogActions><Button onClick={() => setHistoryOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
